import { Panel } from '../../UI/Panel'

import { Container, Graphics, Text } from '@pixi/react'
import { TextStyle } from 'pixi.js'
import { BetHistoryHeaderColPortrait } from './BetHistoryHeaderColPortrait'
import { useMemo } from 'react'
import { BetHistoryFooterPortrait } from './BetHistoryFooterPortrait'
import { Loading } from '../Loading'
import { useBetHistoryStore } from '../../domain/betHistory/store/betHistoryStore'
import { ScrollBox } from '../../UI/ScrollBox'
import { BetHistoryRowData } from '../../domain/betHistory/model/BetHistoryRowData'
import { BetHistoryRowPortrait } from './BetHistoryRowPortrait'
import { CloseButton } from '../CloseButton'
import { Draw } from '../../type/PixiType'
import { t } from '../../domain/language/languageUtil'
import { FixedContainerV2 } from '../../UI'

interface BetHistoryProps {
  openBetDetail: (refNo: number, statementDate: string) => void
  width: number
  height: number
}
const defaultWidth = 720
const defaultHeight = 1280

export const BetHistoryPortrait = (props: BetHistoryProps) => {
  const widthScale = props.width / defaultWidth
  const heightScale = props.height / defaultHeight

  const betHistoryStore = useBetHistoryStore()
  const startTime = betHistoryStore.startTime.split(' ')[0]
  const endTime = betHistoryStore.endTime.split(' ')[0]

  return (
    betHistoryStore.isOpened
      ? (
          <Container scale={[widthScale, heightScale]} eventMode="static">
            <Panel
              x={0}
              y={0}
              width={defaultWidth}
              height={defaultHeight}
              border={{
                color: '#C1C1C1',
                width: 3,
              }}
              radius={15}
            >
              <CloseButton x={660} y={20} width={40} height={40} onPress={() => { useBetHistoryStore.setState({ isOpened: false }) }} />
              <Text
                text={t('betList_title')}
                x={defaultWidth / 2}
                y={50}
                anchor={[0.5, 0.5]}
                style={new TextStyle({
                  fontSize: 36,
                  fontWeight: '600',
                  fill: '#FFFFFF',
                })}
              />
              <Panel
                x={150}
                y={80}
                width={420}
                height={40}
                border={{
                  color: '#AEAEB0',
                  width: 2,
                }}
                radius={90}
                backgroundColor="#161616"
              >
                <Text
                  x={120}
                  y={20}
                  text={startTime}
                  anchor={[0.5, 0.5]}
                  style={new TextStyle({
                    fill: '#C1C1C1',
                    fontSize: 24,
                  })}
                />
                <Text
                  x={210}
                  y={20}
                  text="~"
                  anchor={[0.5, 0.5]}
                  style={new TextStyle({
                    fill: '#C1C1C1',
                    fontSize: 24,
                  })}
                />
                <Text
                  x={300}
                  y={20}
                  text={endTime}
                  anchor={[0.5, 0.5]}
                  style={new TextStyle({
                    fill: '#C1C1C1',
                    fontSize: 24,
                  })}
                />
              </Panel>
              <BetHistoryHeader />
              <BetHistoryBody betList={betHistoryStore.betList} openBetDetail={props.openBetDetail} />
              <BetHistoryFooterPortrait
                totalStake={betHistoryStore.totalStake}
                totalPayout={betHistoryStore.totalPayout}
                betCount={betHistoryStore.betCount}
              />
            </Panel>
          </Container>
        )
      : null
  )
}

const BetHistoryHeader = () => {
  const borderWidth = 2
  const width = 700
  const height = 36
  const colWidth = (width - borderWidth * 2) / 4

  const lines = useMemo(() => {
    const output: Draw[] = []
    for (let i = 0; i < 3; i++) {
      const line: Draw = (g) => {
        g.clear()
        g.lineStyle(2, '#353434') // 2 pixels thick, red color
        const x = colWidth * (i + 1)
        g.moveTo(x, 0)
        g.lineTo(x, 68)
      }
      output.push(line)
    }
    return output
  }, [colWidth])

  return (
    <Panel
      x={10}
      y={150}
      width={width}
      height={height}
      border={{ color: '#404041', width: borderWidth }}
      backgroundColor="#C0C0C0"
    >
      <BetHistoryHeaderColPortrait
        x={borderWidth}
        y={borderWidth}
        width={colWidth}
        height={height - borderWidth * 2}
        text={t('betList_time')}
      />
      <Graphics draw={lines[0]} />
      <BetHistoryHeaderColPortrait
        x={colWidth + borderWidth}
        y={borderWidth}
        width={colWidth}
        height={height - borderWidth * 2}
        text={t('betList_transaction')}
      />
      <Graphics draw={lines[1]} />
      <BetHistoryHeaderColPortrait
        x={colWidth * 2 + borderWidth}
        y={borderWidth}
        width={colWidth}
        height={height - borderWidth * 2}
        text={t('betList_stake')}
      />
      <Graphics draw={lines[2]} />
      <BetHistoryHeaderColPortrait
        x={colWidth * 3 + borderWidth}
        y={borderWidth}
        width={colWidth}
        height={height - borderWidth * 2}
        text={t('betList_payout')}
      />
    </Panel>
  )
}

interface BetHistoryBodyProps {
  betList: BetHistoryRowData[]
  openBetDetail: (refNo: number, statementDate: string) => void
}

const BetHistoryBody = (props: BetHistoryBodyProps) => {
  const [isLoading] = useBetHistoryStore(x => [x.isLoading])

  const children = useMemo(() => {
    return props.betList.map((item, index) => {
      return <BetHistoryRowPortrait key={index} x={0} y={index * 60 + 1} data={item} openBetDetail={props.openBetDetail} />
    })
  }, [props.betList, props.openBetDetail])

  return (
    <FixedContainerV2 name="Bet History Body" x={12} y={185} width={697} height={990}>
      {
        isLoading
          ? <Loading x={360} y={990 / 2} width={100} height={100} />
          : (
              <ScrollBox x={0} y={0} width={697} height={990}>
                {children}
              </ScrollBox>
            )
      }
    </FixedContainerV2>
  )
}
